import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Download, Loader2, LogOut, Mail, Pencil, Search, Trash2, UserPlus, Users, Wallet, CreditCard, X } from "lucide-react";
import { LOGO_URL } from "@/lib/assets";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/lib/api";
import AdminGallery from "@/pages/AdminGallery";
import AdminFixtures from "@/pages/AdminFixtures";
import AdminNominations from "@/pages/AdminNominations";
import AdminCommittee from "@/pages/AdminCommittee";
import AdminSponsors from "@/pages/AdminSponsors";
import AdminStats from "@/pages/AdminStats";
import AdminNews from "@/pages/AdminNews";
import AdminVolunteers from "@/pages/AdminVolunteers";
import AdminProgram from "@/pages/AdminProgram";

const TOKEN_KEY = "mws_admin_token";

const inputCls =
  "w-full bg-clay-bg border border-clay-border focus:border-clay-orange outline-none px-4 py-3 text-clay-text placeholder:text-clay-muted/60 transition-colors duration-300";

function Login({ onLogin }) {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [remember, setRemember] = useState(true);
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await api.post("/admin/login", { email, password, remember });
      // remember me: localStorage survives browser restarts; otherwise session only
      localStorage.removeItem(TOKEN_KEY);
      sessionStorage.removeItem(TOKEN_KEY);
      (remember ? localStorage : sessionStorage).setItem(TOKEN_KEY, data.token);
      onLogin(data.token);
      toast.success(`Welcome back, ${data.name}`);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  return (
    <main className="min-h-screen flex items-center justify-center px-4 bg-clay-bg">
      <motion.form
        data-testid="admin-login-form"
        onSubmit={submit}
        initial={{ opacity: 0, y: 40 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
        className="max-w-sm w-full border border-clay-border bg-clay-card p-8 sm:p-10"
      >
        <div className="flex items-center gap-3 mb-8">
          <img src={LOGO_URL} alt="Mid Western Speedway logo" className="w-10 h-10 object-cover rounded-sm ring-1 ring-clay-border" />
          <div>
            <h1 className="font-display font-bold uppercase text-2xl tracking-tight leading-none">Club Admin</h1>
            <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-clay-muted mt-1">Command Center</p>
          </div>
        </div>
        <div className="space-y-4">
          <input data-testid="admin-email-input" type="text" placeholder="Email or username" value={email} onChange={(e) => setEmail(e.target.value)} className={inputCls} required />
          <input data-testid="admin-password-input" type="password" placeholder="Password" value={password} onChange={(e) => setPassword(e.target.value)} className={inputCls} required />
          <label
            data-testid="admin-remember-toggle"
            className="flex items-center gap-3 border border-clay-border bg-clay-bg px-4 py-3 cursor-pointer hover:border-clay-orange/60 transition-colors"
          >
            <input
              type="checkbox"
              checked={remember}
              onChange={(e) => setRemember(e.target.checked)}
              className="w-4 h-4 accent-[#E65100]"
            />
            <span className="text-sm">
              Remember me on this device
              <span className="block font-mono text-[10px] uppercase tracking-[0.2em] text-clay-muted mt-0.5">
                Stays signed in for 30 days — untick on shared computers
              </span>
            </span>
          </label>
          <button
            data-testid="admin-login-button"
            disabled={loading}
            className="w-full bg-clay-orange hover:bg-clay-gold disabled:opacity-60 text-clay-bg font-display font-bold uppercase tracking-wider text-lg px-6 py-4 transition-colors duration-300 flex items-center justify-center gap-2"
          >
            {loading && <Loader2 className="w-5 h-5 animate-spin" />}
            Sign In
          </button>
        </div>
        <a href="/" className="block text-center mt-6 font-mono text-[11px] uppercase tracking-[0.25em] text-clay-muted hover:text-clay-gold transition-colors">
          ← Back to site
        </a>
      </motion.form>
    </main>
  );
}

export default function Admin() {
  const [token, setToken] = useState(() => localStorage.getItem(TOKEN_KEY) || sessionStorage.getItem(TOKEN_KEY));
  const [stats, setStats] = useState(null);
  const [members, setMembers] = useState([]);
  const [payments, setPayments] = useState([]);
  const [search, setSearch] = useState("");
  const [view, setView] = useState("members");
  const [showAdd, setShowAdd] = useState(false);
  const [newMember, setNewMember] = useState({ name: "", email: "", phone: "", address_street: "", address_suburb: "", address_postcode: "", racing_class: "Spectator / Supporter", tier: "Adult Single", paid: false, registering_car: false, car_details: "", insurance: "None", wwcc: false, wwcc_card: "", wwcc_expiry: "" });
  const [adding, setAdding] = useState(false);
  const [editId, setEditId] = useState(null);
  const [payFilter, setPayFilter] = useState("all");
  const [showRecord, setShowRecord] = useState(false);
  const [recordForm, setRecordForm] = useState({ name: "", email: "", amount: "", kind: "donation", method: "manual", note: "" });
  const [recording, setRecording] = useState(false);
  const [contacts, setContacts] = useState([]);

  const headers = { Authorization: `Bearer ${token}` };

  const [emailTarget, setEmailTarget] = useState(null); // null | "all" | member object
  const [emailForm, setEmailForm] = useState({ subject: "", body: "" });
  const [emailBusy, setEmailBusy] = useState(false);
  const [addressBlastBusy, setAddressBlastBusy] = useState(false);

  const sendAddressBlast = async () => {
    if (!window.confirm(`Email ALL ${members.length} members asking them to update their address? Each gets a personal update link, or they can just reply.`)) return;
    setAddressBlastBusy(true);
    try {
      const { data } = await api.post("/admin/members/address-update-blast", {}, { headers });
      toast.success(`Address update email sent to ${data.sent} member${data.sent === 1 ? "" : "s"}${data.failed ? ` — ${data.failed} failed` : ""}`);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setAddressBlastBusy(false);
    }
  };

  const sendMemberEmail = async () => {
    if (!emailForm.subject.trim() || !emailForm.body.trim()) return;
    setEmailBusy(true);
    try {
      if (emailTarget === "all") {
        const { data } = await api.post("/admin/members/email-blast", emailForm, { headers });
        toast.success(`Email sent to ${data.sent} member${data.sent === 1 ? "" : "s"}${data.failed ? ` — ${data.failed} failed` : ""}`);
      } else {
        await api.post(`/admin/members/${emailTarget.id}/email`, emailForm, { headers });
        toast.success(`Email sent to ${emailTarget.name}`);
      }
      setEmailTarget(null);
      setEmailForm({ subject: "", body: "" });
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setEmailBusy(false);
    }
  };

  useEffect(() => {
    if (!token) return;
    const load = async () => {
      try {
        const [s, m, p, c] = await Promise.all([
          api.get("/admin/stats", { headers }),
          api.get("/admin/members", { headers }),
          api.get("/admin/payments", { headers }),
          api.get("/admin/contacts", { headers }),
        ]);
        setStats(s.data);
        setMembers(m.data);
        setPayments(p.data);
        setContacts(c.data);
      } catch (err) {
        if (err.response?.status === 401) {
          localStorage.removeItem(TOKEN_KEY);
          sessionStorage.removeItem(TOKEN_KEY);
          setToken(null);
        } else {
          toast.error("Failed to load dashboard");
        }
      }
    };
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  if (!token) return <Login onLogin={setToken} />;

  const filtered = members.filter(
    (m) =>
      m.name?.toLowerCase().includes(search.toLowerCase()) ||
      m.email?.toLowerCase().includes(search.toLowerCase()) ||
      m.tier?.toLowerCase().includes(search.toLowerCase())
  );

  const filteredPayments = payments.filter((p) =>
    payFilter === "all" ? true : payFilter === "manual" ? p.method === "manual" : payFilter === "square" ? p.method === "square" : p.method !== "manual" && p.method !== "square"
  );

  const payType = (p) =>
    p.lookup_key === "donation" ? "Donation" : p.lookup_key?.startsWith("sponsor") ? "Sponsorship" : "Membership";

  const methodLabel = (m) => (m === "manual" ? "Cash/Bank" : m === "square" ? "Square" : "Stripe");

  const recordPayment = async (e) => {
    e.preventDefault();
    const amount = parseFloat(recordForm.amount);
    if (!recordForm.name.trim() || !amount || amount <= 0) {
      toast.error("Name and a valid amount are required");
      return;
    }
    setRecording(true);
    try {
      await api.post("/admin/payments/record", { ...recordForm, amount }, { headers });
      toast.success(`Recorded $${amount.toFixed(2)} from ${recordForm.name}`);
      setRecordForm({ name: "", email: "", amount: "", kind: "donation", method: "manual", note: "" });
      setShowRecord(false);
      const [p, s] = await Promise.all([
        api.get("/admin/payments", { headers }),
        api.get("/admin/stats", { headers }),
      ]);
      setPayments(p.data);
      setStats(s.data);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setRecording(false);
    }
  };

  const deletePayment = async (p) => {
    if (!window.confirm(`Delete the payment record for ${p.member_name} ($${p.amount?.toFixed(2)})? This only removes the record here — any real payment stays in Square.`)) return;
    try {
      await api.delete(`/admin/payments/${p.session_id}`, { headers });
      setPayments((prev) => prev.filter((x) => x.session_id !== p.session_id));
      const { data } = await api.get("/admin/stats", { headers });
      setStats(data);
      toast.success("Payment record deleted");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const exportCsv = async () => {
    const res = await api.get("/admin/export.csv", { headers, responseType: "blob" });
    const url = URL.createObjectURL(res.data);
    const a = document.createElement("a");
    a.href = url;
    a.download = "mws_members.csv";
    a.click();
    URL.revokeObjectURL(url);
  };

  const logout = () => {
    localStorage.removeItem(TOKEN_KEY);
    setToken(null);
  };

  const sendReminders = async () => {
    if (!window.confirm("Email a renewal reminder to every active member who hasn't received one yet?")) return;
    try {
      const { data } = await api.post("/admin/reminders/renewal", {}, { headers });
      toast.success(`Renewal reminders sent to ${data.sent} of ${data.eligible} members`);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const saveMember = async (e) => {
    e.preventDefault();
    if (!newMember.name.trim() || !newMember.email.trim() || !newMember.phone.trim() || !newMember.address_street.trim() || !newMember.address_suburb.trim() || !newMember.address_postcode.trim()) {
      toast.error("Name, email, phone and full mailing address are required");
      return;
    }
    setAdding(true);
    try {
      if (editId) {
        await api.put(`/admin/members/${editId}`, { ...newMember, mark_paid: newMember.paid }, { headers });
        toast.success("Member updated");
      } else {
        await api.post("/admin/members", newMember, { headers });
        toast.success(`${newMember.name} added as ${newMember.paid || newMember.tier === "Kids Under 12 (Free)" ? "an active" : "a pending"} member`);
      }
      setNewMember({ name: "", email: "", phone: "", address_street: "", address_suburb: "", address_postcode: "", racing_class: "Spectator / Supporter", tier: "Adult Single", paid: false, registering_car: false, car_details: "", insurance: "None", wwcc: false, wwcc_card: "", wwcc_expiry: "" });
      setShowAdd(false);
      setEditId(null);
      const { data } = await api.get("/admin/members", { headers });
      setMembers(data);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setAdding(false);
    }
  };

  const startEdit = (m) => {
    setNewMember({
      name: m.name || "",
      email: m.email || "",
      phone: m.phone || "",
      address_street: m.address_street || "",
      address_suburb: m.address_suburb || "",
      address_postcode: m.address_postcode || "",
      racing_class: m.racing_class || "Spectator / Supporter",
      tier: m.tier || "Adult Single",
      paid: false,
      registering_car: !!m.registering_car,
      car_details: m.car_details || "",
      insurance: m.insurance || "None",
      wwcc: !!m.wwcc,
      wwcc_card: m.wwcc_card || "",
      wwcc_expiry: m.wwcc_expiry || "",
    });
    setEditId(m.id);
    setShowAdd(true);
  };

  const deleteMember = async (m) => {
    if (!window.confirm(`Remove ${m.name} from the roster? This cannot be undone.`)) return;
    try {
      await api.delete(`/admin/members/${m.id}`, { headers });
      toast.success(`${m.name} removed`);
      setMembers((prev) => prev.filter((x) => x.id !== m.id));
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const deleteContact = async (c) => {
    if (!window.confirm(`Delete message from ${c.name}?`)) return;
    try {
      await api.delete(`/admin/contacts/${c.id}`, { headers });
      toast.success("Message deleted");
      setContacts((prev) => prev.filter((x) => x.id !== c.id));
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const statCards = [
    { icon: Users, label: "Total Members", value: stats?.members_total ?? "—", testid: "stat-members" },
    { icon: CreditCard, label: "Active (Paid)", value: stats?.members_active ?? "—", testid: "stat-active" },
    { icon: UserPlus, label: "Pending", value: stats ? stats.members_total - stats.members_active : "—", testid: "stat-pending" },
  ];

  const paymentCards = [
    { icon: Wallet, label: "Revenue (AUD)", value: stats ? `$${stats.revenue_paid.toFixed(2)}` : "—", testid: "stat-revenue" },
    { icon: CreditCard, label: "Paid", value: payments.filter((p) => p.payment_status === "paid").length, testid: "stat-paid-count" },
    { icon: Loader2, label: "Pending / Unpaid", value: payments.filter((p) => p.payment_status !== "paid").length, testid: "stat-unpaid-count" },
  ];

  return (
    <main data-testid="admin-dashboard" className="min-h-screen bg-clay-bg">
      <header className="border-b border-clay-border bg-clay-surface/60 backdrop-blur-xl sticky top-0 z-40">
        <div className="max-w-[1400px] mx-auto px-4 sm:px-8 py-3 sm:py-4 flex flex-wrap items-center justify-between gap-x-4 gap-y-2">
          <div className="flex items-center gap-3 min-w-0">
            <img src={LOGO_URL} alt="Mid Western Speedway logo" className="w-9 h-9 object-cover rounded-sm ring-1 ring-clay-border shrink-0" />
            <span className="font-display font-bold uppercase text-base sm:text-lg tracking-wide truncate">Command Center</span>
          </div>
          <div className="flex items-center gap-3 sm:gap-4">
            <a href="/" className="font-mono text-[11px] uppercase tracking-[0.25em] text-clay-muted hover:text-clay-gold transition-colors">
              View Site
            </a>
            <button data-testid="admin-logout-button" onClick={logout} className="flex items-center gap-2 font-mono text-[11px] uppercase tracking-[0.25em] text-clay-muted hover:text-clay-orange transition-colors">
              <LogOut className="w-4 h-4" /> Logout
            </button>
          </div>
        </div>
      </header>

      <div className="max-w-[1400px] mx-auto px-4 sm:px-8 pt-6 sm:pt-8 grid grid-cols-3 sm:grid-cols-4 lg:flex lg:flex-wrap gap-2">
        {[
          ["members", "Members", "admin-tab-members"],
          ["payments", "Payments", "admin-tab-payments"],
          ["gallery", "Gallery Manager", "admin-tab-gallery"],
          ["fixtures", "Calendar Editor", "admin-tab-fixtures"],
          ["nominations", "Nominations", "admin-tab-nominations"],
          ["news", "News", "admin-tab-news"],
          ["volunteers", "Volunteers", "admin-tab-volunteers"],
          ["program", "Program", "admin-tab-program"],
          ["committee", "Committee", "admin-tab-committee"],
          ["sponsors", "Sponsors", "admin-tab-sponsors"],
          ["stats", "Site Stats", "admin-tab-stats"],
        ].map(([key, label, testid]) => (
          <button
            key={key}
            data-testid={testid}
            onClick={() => setView(key)}
            className={`font-display font-bold uppercase tracking-wider px-2 py-3 sm:px-6 text-sm sm:text-base border transition-all duration-300 ${
              view === key
                ? "bg-clay-orange text-clay-bg border-clay-orange"
                : "border-clay-border text-clay-muted hover:border-clay-orange/60 hover:text-clay-text"
            }`}
          >
            {label}
          </button>
        ))}
      </div>

      {view === "members" && (
      <div className="max-w-[1400px] mx-auto px-4 sm:px-8 py-10 space-y-12">
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-5">
          {statCards.map((s, i) => (
            <motion.div
              key={s.label}
              data-testid={s.testid}
              initial={{ opacity: 0, y: 24 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: i * 0.1 }}
              className="border border-clay-border bg-clay-card p-6"
            >
              <s.icon className="w-6 h-6 text-clay-orange mb-4" />
              <p className="font-display font-black text-4xl">{s.value}</p>
              <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-clay-muted mt-2">{s.label}</p>
            </motion.div>
          ))}
        </div>

        <section>
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-5">
            <h2 className="font-display font-extrabold uppercase text-3xl tracking-tight">Member Roster</h2>
            <div className="flex flex-wrap items-center gap-3">
              <button
                data-testid="admin-add-member-button"
                onClick={() => setShowAdd(!showAdd)}
                className="flex items-center gap-2 bg-clay-orange hover:bg-clay-gold text-clay-bg font-mono text-xs uppercase tracking-[0.2em] px-4 py-2.5 transition-colors"
              >
                <UserPlus className="w-4 h-4" /> Add Member
              </button>
              <div className="relative">
                <Search className="w-4 h-4 text-clay-muted absolute left-3 top-1/2 -translate-y-1/2" />
                <input
                  data-testid="admin-search-input"
                  placeholder="Search members…"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  className="bg-clay-bg border border-clay-border focus:border-clay-orange outline-none pl-9 pr-4 py-2.5 text-sm w-56 transition-colors"
                />
              </div>
              <button
                data-testid="admin-reminders-button"
                onClick={sendReminders}
                className="flex items-center gap-2 border border-clay-border hover:border-clay-gold font-mono text-xs uppercase tracking-[0.2em] px-4 py-2.5 transition-colors"
              >
                <Mail className="w-4 h-4" /> Reminders
              </button>
              <button
                data-testid="admin-email-all-button"
                onClick={() => setEmailTarget("all")}
                className="flex items-center gap-2 border border-clay-border hover:border-clay-orange font-mono text-xs uppercase tracking-[0.2em] px-4 py-2.5 transition-colors"
              >
                <Mail className="w-4 h-4" /> Email All
              </button>
              <button
                data-testid="admin-address-blast-button"
                onClick={sendAddressBlast}
                disabled={addressBlastBusy}
                className="flex items-center gap-2 border border-clay-border hover:border-clay-gold font-mono text-xs uppercase tracking-[0.2em] px-4 py-2.5 transition-colors"
              >
                <Mail className="w-4 h-4" /> Address Update Email
              </button>
              <button
                data-testid="admin-export-csv-button"
                onClick={exportCsv}
                className="flex items-center gap-2 border border-clay-border hover:border-clay-orange font-mono text-xs uppercase tracking-[0.2em] px-4 py-2.5 transition-colors"
              >
                <Download className="w-4 h-4" /> CSV
              </button>
            </div>
          </div>
          {showAdd && (
            <form
              data-testid="admin-add-member-form"
              onSubmit={saveMember}
              className="border border-clay-border bg-clay-card p-6 mb-5 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4"
            >
              <input
                data-testid="add-member-name-input"
                placeholder="Full name *"
                value={newMember.name}
                onChange={(e) => setNewMember((m) => ({ ...m, name: e.target.value }))}
                className="bg-clay-bg border border-clay-border focus:border-clay-orange outline-none px-4 py-3 transition-colors"
                required
              />
              <input
                data-testid="add-member-email-input"
                type="email"
                placeholder="Email *"
                value={newMember.email}
                onChange={(e) => setNewMember((m) => ({ ...m, email: e.target.value }))}
                className="bg-clay-bg border border-clay-border focus:border-clay-orange outline-none px-4 py-3 transition-colors"
                required
              />
              <input
                data-testid="add-member-phone-input"
                placeholder="Phone (optional)"
                value={newMember.phone}
                onChange={(e) => setNewMember((m) => ({ ...m, phone: e.target.value }))}
                className="bg-clay-bg border border-clay-border focus:border-clay-orange outline-none px-4 py-3 transition-colors"
              />
              <input
                data-testid="add-member-address-street-input"
                placeholder="Street address (optional)"
                value={newMember.address_street}
                onChange={(e) => setNewMember((m) => ({ ...m, address_street: e.target.value }))}
                className="bg-clay-bg border border-clay-border focus:border-clay-orange outline-none px-4 py-3 transition-colors"
              />
              <input
                data-testid="add-member-address-suburb-input"
                placeholder="Suburb / Town (optional)"
                value={newMember.address_suburb}
                onChange={(e) => setNewMember((m) => ({ ...m, address_suburb: e.target.value }))}
                className="bg-clay-bg border border-clay-border focus:border-clay-orange outline-none px-4 py-3 transition-colors"
              />
              <input
                data-testid="add-member-address-postcode-input"
                placeholder="Postcode (optional)"
                value={newMember.address_postcode}
                onChange={(e) => setNewMember((m) => ({ ...m, address_postcode: e.target.value }))}
                className="bg-clay-bg border border-clay-border focus:border-clay-orange outline-none px-4 py-3 transition-colors"
              />
              <select
                data-testid="add-member-class-select"
                value={newMember.racing_class}
                onChange={(e) => setNewMember((m) => ({ ...m, racing_class: e.target.value }))}
                className="bg-clay-bg border border-clay-border focus:border-clay-orange outline-none px-4 py-3 transition-colors"
              >
                {["Spectator / Supporter", "Formula 500s", "Wingless Sprints", "Street Stocks", "Super Rods", "Junior Karts / JQMV", "Other"].map((c) => (
                  <option key={c} value={c}>{c}</option>
                ))}
              </select>
              <select
                data-testid="add-member-tier-select"
                value={newMember.tier}
                onChange={(e) => setNewMember((m) => ({ ...m, tier: e.target.value }))}
                className="bg-clay-bg border border-clay-border focus:border-clay-orange outline-none px-4 py-3 transition-colors"
              >
                {["Adult Single", "Junior Single (12-18)", "Family (2 Adults + 2 Children)", "Kids Under 12 (Free)"].map((t) => (
                  <option key={t} value={t}>{t}</option>
                ))}
              </select>
              <select
                data-testid="add-member-insurance-select"
                value={newMember.insurance}
                onChange={(e) => setNewMember((m) => ({ ...m, insurance: e.target.value }))}
                className="bg-clay-bg border border-clay-border focus:border-clay-orange outline-none px-4 py-3 transition-colors"
              >
                {["None", "Speedway Australia", "AASA", "Both — Speedway Australia & AASA"].map((i) => (
                  <option key={i} value={i}>Insurance: {i}</option>
                ))}
              </select>
              <select
                data-testid="add-member-car-select"
                value={newMember.registering_car ? "yes" : "no"}
                onChange={(e) => setNewMember((m) => ({ ...m, registering_car: e.target.value === "yes" }))}
                className="bg-clay-bg border border-clay-border focus:border-clay-orange outline-none px-4 py-3 transition-colors"
              >
                <option value="no">Registering a car: No</option>
                <option value="yes">Registering a car: Yes</option>
              </select>
              {newMember.registering_car && (
                <input
                  data-testid="add-member-car-details-input"
                  placeholder="Car number and class — e.g. V42 Street Stock"
                  value={newMember.car_details}
                  onChange={(e) => setNewMember((m) => ({ ...m, car_details: e.target.value }))}
                  className="bg-clay-bg border border-clay-border focus:border-clay-orange outline-none px-4 py-3 transition-colors"
                />
              )}
              <select
                data-testid="add-member-wwcc-select"
                value={newMember.wwcc ? "yes" : "no"}
                onChange={(e) => setNewMember((m) => ({ ...m, wwcc: e.target.value === "yes" }))}
                className="bg-clay-bg border border-clay-border focus:border-clay-orange outline-none px-4 py-3 transition-colors"
              >
                <option value="no">WWCC: No</option>
                <option value="yes">WWCC: Yes</option>
              </select>
              {newMember.wwcc && (
                <>
                  <input
                    data-testid="add-member-wwcc-card-input"
                    placeholder="WWCC card number"
                    value={newMember.wwcc_card}
                    onChange={(e) => setNewMember((m) => ({ ...m, wwcc_card: e.target.value }))}
                    className="bg-clay-bg border border-clay-border focus:border-clay-orange outline-none px-4 py-3 transition-colors"
                  />
                  <label className="font-mono text-[10px] uppercase tracking-[0.2em] text-clay-muted">
                    WWCC expiry
                    <input
                      data-testid="add-member-wwcc-expiry-input"
                      type="date"
                      value={newMember.wwcc_expiry}
                      onChange={(e) => setNewMember((m) => ({ ...m, wwcc_expiry: e.target.value }))}
                      className="bg-clay-bg border border-clay-border focus:border-clay-orange outline-none px-4 py-3 transition-colors mt-1.5 w-full"
                    />
                  </label>
                </>
              )}
              <label
                data-testid="add-member-paid-toggle"
                className="sm:col-span-2 lg:col-span-3 flex items-center gap-3 border border-clay-border bg-clay-bg px-4 py-3 cursor-pointer hover:border-clay-orange/60 transition-colors"
              >
                <input
                  type="checkbox"
                  checked={newMember.paid}
                  onChange={(e) => setNewMember((m) => ({ ...m, paid: e.target.checked }))}
                  className="w-4 h-4 accent-[#E65100]"
                />
                <span className="text-sm">
                  {editId ? "Mark as paid — cash/bank transfer received" : "Payment received on site (cash / bank transfer)"}
                  <span className="block font-mono text-[10px] uppercase tracking-[0.2em] text-clay-muted mt-0.5">
                    {editId
                      ? "Creates a manual payment record if none exists and activates the member"
                      : "Tick if they paid in person — records the amount and marks them active"}
                  </span>
                </span>
              </label>
              <button
                data-testid="add-member-save-button"
                disabled={adding}
                className="bg-clay-orange hover:bg-clay-gold disabled:opacity-60 text-clay-bg font-display font-bold uppercase tracking-wider px-6 py-3 transition-colors flex items-center justify-center gap-2"
              >
                {adding && <Loader2 className="w-4 h-4 animate-spin" />}
                {editId ? "Save Changes" : "Save Member"}
              </button>
              {editId && (
                <button
                  data-testid="edit-member-cancel-button"
                  type="button"
                  onClick={() => { setEditId(null); setShowAdd(false); setNewMember({ name: "", email: "", phone: "", address_street: "", address_suburb: "", address_postcode: "", racing_class: "Spectator / Supporter", tier: "Adult Single", paid: false, registering_car: false, car_details: "", insurance: "None", wwcc: false, wwcc_card: "", wwcc_expiry: "" }); }}
                  className="border border-clay-border hover:border-clay-orange font-display font-bold uppercase tracking-wider px-6 py-3 transition-colors"
                >
                  Cancel
                </button>
              )}
              <p className="sm:col-span-2 lg:col-span-3 font-mono text-[11px] uppercase tracking-[0.2em] text-clay-muted">
                {editId
                  ? "Editing member details — payment history is kept as-is unless you tick mark as paid."
                  : "For cash or bank-transfer signups at the track — no Stripe involved, no email sent."}
              </p>
            </form>
          )}
          <div className="border border-clay-border overflow-x-auto">
            <table className="w-full text-sm min-w-[720px]">
              <thead>
                <tr className="border-b border-clay-border bg-clay-surface font-mono text-[10px] uppercase tracking-[0.2em] text-clay-muted text-left">
                  <th className="px-4 py-3">Tier</th>
                  <th className="px-4 py-3">Name</th>
                  <th className="px-4 py-3">Email</th>
                  <th className="px-4 py-3">Address</th>
                  <th className="px-4 py-3">Class</th>
                  <th className="px-4 py-3">Checks</th>
                  <th className="px-4 py-3">Status</th>
                  <th className="px-4 py-3">Joined</th>
                  <th className="px-4 py-3">Actions</th>
                </tr>
              </thead>
              <tbody>
                {filtered.length === 0 && (
                  <tr>
                    <td className="px-4 py-10 text-center text-clay-muted font-mono text-xs uppercase tracking-[0.2em]" colSpan={9}>
                      No members yet — share the Join button!
                    </td>
                  </tr>
                )}
                {filtered.map((m) => (
                  <tr key={m.id} data-testid="admin-member-row" className="border-b border-clay-border/50 hover:bg-clay-card/60 transition-colors">
                    <td className="px-4 py-3 font-mono text-clay-gold">{m.tier || "—"}</td>
                    <td className="px-4 py-3 font-medium">{m.name}</td>
                    <td className="px-4 py-3 text-clay-muted">{m.email}</td>
                    <td className="px-4 py-3 text-clay-muted text-xs" data-testid="member-address-cell">
                      {[m.address_street, m.address_suburb, m.address_postcode].filter(Boolean).join(", ") || <span className="text-red-400/70 font-mono text-[10px] uppercase tracking-wide">Missing</span>}
                    </td>
                    <td className="px-4 py-3 text-clay-muted">{m.racing_class}</td>
                    <td className="px-4 py-3">
                      <div className="flex gap-1">
                        <span
                          title={m.insurance && m.insurance !== "None" ? `Insurance: ${m.insurance}` : "No insurance recorded"}
                          className={`font-mono text-[9px] uppercase tracking-wide px-1.5 py-0.5 border ${
                            m.insurance && m.insurance !== "None"
                              ? "border-green-500/40 text-green-400"
                              : "border-red-500/30 text-red-400/70"
                          }`}
                        >
                          {m.insurance === "AASA" ? "AASA" : m.insurance === "Speedway Australia" ? "SA Ins" : (m.insurance || "").startsWith("Both") ? "Both" : "No Ins"}
                        </span>
                        <span
                          title={m.wwcc ? `WWCC${m.wwcc_card ? `: ${m.wwcc_card}` : ""}` : "No WWCC recorded"}
                          className={`font-mono text-[9px] uppercase tracking-wide px-1.5 py-0.5 border ${
                            m.wwcc ? "border-green-500/40 text-green-400" : "border-red-500/30 text-red-400/70"
                          }`}
                        >
                          {m.wwcc ? "WWCC" : "No WWCC"}
                        </span>
                        {m.wwcc && m.wwcc_expiry && (
                          <span
                            data-testid="member-wwcc-expiry"
                            className={`font-mono text-[9px] uppercase tracking-wide px-1.5 py-0.5 border ${
                              m.wwcc_expiry < new Date().toLocaleDateString("en-CA")
                                ? "border-red-500/50 text-red-400"
                                : "border-clay-border text-clay-muted"
                            }`}
                          >
                            {m.wwcc_expiry < new Date().toLocaleDateString("en-CA") ? "WWCC expired" : `WWCC exp ${m.wwcc_expiry}`}
                          </span>
                        )}
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <span className={`font-mono text-[10px] uppercase tracking-[0.2em] px-2 py-1 border ${m.status === "active" ? "border-green-500/40 text-green-400" : "border-amber-500/40 text-amber-400"}`}>
                        {m.status}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-clay-muted">{m.created_at?.slice(0, 10)}</td>
                    <td className="px-4 py-3">
                      <div className="flex gap-1">
                        <button
                          data-testid="member-email-button"
                          onClick={() => setEmailTarget(m)}
                          className="text-clay-muted hover:text-clay-orange transition-colors p-1.5"
                          aria-label="Email member"
                        >
                          <Mail className="w-4 h-4" />
                        </button>
                        <button
                          data-testid="member-edit-button"
                          onClick={() => startEdit(m)}
                          className="text-clay-muted hover:text-clay-gold transition-colors p-1.5"
                          aria-label="Edit member"
                        >
                          <Pencil className="w-4 h-4" />
                        </button>
                        <button
                          data-testid="member-delete-button"
                          onClick={() => deleteMember(m)}
                          className="text-clay-muted hover:text-red-400 transition-colors p-1.5"
                          aria-label="Delete member"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>

        <section>
          <h2 className="font-display font-extrabold uppercase text-3xl tracking-tight mb-5">
            Contact Messages <span className="text-clay-muted text-xl">({contacts.length})</span>
          </h2>
          {contacts.length === 0 ? (
            <p className="font-mono text-xs uppercase tracking-[0.25em] text-clay-muted border border-dashed border-clay-border py-12 text-center">
              No messages yet — the contact form emails the club too
            </p>
          ) : (
            <div className="space-y-3">
              {contacts.map((c) => (
                <div key={c.id} data-testid="contact-message-row" className="border border-clay-border bg-clay-card p-5 flex items-start justify-between gap-4">
                  <div className="min-w-0">
                    <div className="flex flex-wrap items-center gap-3 mb-1.5">
                      <span className="font-display font-bold uppercase tracking-wide">{c.name}</span>
                      <a href={`mailto:${c.email}`} className="font-mono text-xs text-clay-gold hover:underline">{c.email}</a>
                      {c.phone && <a href={`tel:${c.phone}`} className="font-mono text-xs text-clay-muted hover:text-clay-text">{c.phone}</a>}
                      <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-clay-muted">{c.created_at?.slice(0, 10)}</span>
                    </div>
                    <p className="text-clay-muted text-sm leading-relaxed whitespace-pre-wrap">{c.message}</p>
                  </div>
                  <button
                    data-testid="contact-delete-button"
                    onClick={() => deleteContact(c)}
                    className="text-clay-muted hover:text-red-400 transition-colors shrink-0 p-1.5"
                    aria-label="Delete message"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              ))}
            </div>
          )}
        </section>
      </div>
      )}

      {view === "payments" && (
      <div className="max-w-[1400px] mx-auto px-4 sm:px-8 py-10 space-y-12">
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-5">
          {paymentCards.map((s, i) => (
            <motion.div
              key={s.label}
              data-testid={s.testid}
              initial={{ opacity: 0, y: 24 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: i * 0.1 }}
              className="border border-clay-border bg-clay-card p-6"
            >
              <s.icon className="w-6 h-6 text-clay-orange mb-4" />
              <p className="font-display font-black text-4xl">{s.value}</p>
              <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-clay-muted mt-2">{s.label}</p>
            </motion.div>
          ))}
        </div>

        <section>
          <div className="flex flex-wrap items-center justify-between gap-4 mb-5">
            <h2 className="font-display font-extrabold uppercase text-3xl tracking-tight">Payment History</h2>
            <div className="flex flex-wrap gap-2 items-center">
              <button
                data-testid="payment-record-toggle"
                onClick={() => setShowRecord((s) => !s)}
                className="flex items-center gap-2 bg-clay-orange hover:bg-clay-gold text-clay-bg font-mono text-xs uppercase tracking-[0.2em] px-4 py-2 transition-colors"
              >
                {showRecord ? <X className="w-4 h-4" /> : <UserPlus className="w-4 h-4" />}
                {showRecord ? "Close" : "Record Payment"}
              </button>
              <div className="flex gap-2" data-testid="payment-method-filter">
              {[
                ["all", "All"],
                ["square", "Square"],
                ["manual", "Cash / Bank"],
              ].map(([key, label]) => (
                <button
                  key={key}
                  data-testid={`payment-filter-${key}`}
                  onClick={() => setPayFilter(key)}
                  className={`font-mono text-xs uppercase tracking-[0.2em] px-4 py-2 border transition-colors duration-300 ${
                    payFilter === key
                      ? "bg-clay-orange text-clay-bg border-clay-orange"
                      : "border-clay-border text-clay-muted hover:border-clay-orange/60 hover:text-clay-text"
                  }`}
                >
                  {label}
                </button>
              ))}
            </div>
          </div>
          </div>
          {showRecord && (
            <form
              data-testid="payment-record-form"
              onSubmit={recordPayment}
              className="border border-clay-border bg-clay-card p-6 sm:p-8 mb-6"
            >
              <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-clay-muted mb-5">
                Manually record money received — cash/bank at the track, or a Square payment missing from this list
              </p>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                <input data-testid="record-name-input" placeholder="Name *" value={recordForm.name} onChange={(e) => setRecordForm((f) => ({ ...f, name: e.target.value }))} className={inputCls} required />
                <input data-testid="record-amount-input" type="number" step="0.01" min="0.5" placeholder="Amount (AUD) *" value={recordForm.amount} onChange={(e) => setRecordForm((f) => ({ ...f, amount: e.target.value }))} className={inputCls} required />
                <input data-testid="record-email-input" type="email" placeholder="Email (optional)" value={recordForm.email} onChange={(e) => setRecordForm((f) => ({ ...f, email: e.target.value }))} className={inputCls} />
                <select data-testid="record-kind-select" value={recordForm.kind} onChange={(e) => setRecordForm((f) => ({ ...f, kind: e.target.value }))} className={inputCls}>
                  <option value="donation">Donation</option>
                  <option value="membership">Membership</option>
                  <option value="sponsorship">Sponsorship</option>
                </select>
                <select data-testid="record-method-select" value={recordForm.method} onChange={(e) => setRecordForm((f) => ({ ...f, method: e.target.value }))} className={inputCls}>
                  <option value="manual">Cash / Bank</option>
                  <option value="square">Square</option>
                </select>
                <input data-testid="record-note-input" placeholder="Note (optional)" value={recordForm.note} onChange={(e) => setRecordForm((f) => ({ ...f, note: e.target.value }))} className={inputCls} />
              </div>
              <button
                data-testid="record-save-button"
                disabled={recording}
                className="mt-5 bg-clay-orange hover:bg-clay-gold disabled:opacity-60 text-clay-bg font-display font-bold uppercase tracking-wider px-8 py-3.5 transition-colors duration-300 flex items-center gap-2"
              >
                {recording && <Loader2 className="w-4 h-4 animate-spin" />}
                Record as Paid
              </button>
            </form>
          )}
          <div className="border border-clay-border overflow-x-auto">
            <table className="w-full text-sm min-w-[720px]">
              <thead>
                <tr className="border-b border-clay-border bg-clay-surface font-mono text-[10px] uppercase tracking-[0.2em] text-clay-muted text-left">
                  <th className="px-4 py-3">Type</th>
                  <th className="px-4 py-3">Name</th>
                  <th className="px-4 py-3">Method</th>
                  <th className="px-4 py-3">Amount</th>
                  <th className="px-4 py-3">Status</th>
                  <th className="px-4 py-3">Date</th>
                  <th className="px-4 py-3">Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredPayments.length === 0 && (
                  <tr>
                    <td colSpan={7} className="px-4 py-10 text-center text-clay-muted font-mono text-xs uppercase tracking-[0.2em]">
                      No payments yet — memberships, donations and sponsorships all land here
                    </td>
                  </tr>
                )}
                {filteredPayments.map((p) => (
                  <tr key={p.session_id} data-testid="admin-payment-row" className="border-b border-clay-border/50 hover:bg-clay-card/60 transition-colors">
                    <td className="px-4 py-3">
                      <span className="font-mono text-[10px] uppercase tracking-[0.2em] px-2 py-1 border border-clay-border text-clay-muted">
                        {payType(p)}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      {p.member_name || p.business_name}
                      {p.package_label && <span className="block font-mono text-[10px] text-clay-muted mt-0.5">{p.package_label}</span>}
                    </td>
                    <td className="px-4 py-3">
                      <span className={`font-mono text-[10px] uppercase tracking-[0.2em] px-2 py-1 border ${p.method === "manual" ? "border-clay-gold/40 text-clay-gold" : "border-sky-500/40 text-sky-400"}`}>
                        {methodLabel(p.method)}
                      </span>
                    </td>
                    <td className="px-4 py-3 font-mono text-clay-gold">${p.amount?.toFixed(2)} {p.currency?.toUpperCase()}</td>
                    <td className="px-4 py-3">
                      <span className={`font-mono text-[10px] uppercase tracking-[0.2em] px-2 py-1 border ${p.payment_status === "paid" ? "border-green-500/40 text-green-400" : "border-amber-500/40 text-amber-400"}`}>
                        {p.payment_status}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-clay-muted">{p.created_at?.slice(0, 10)}</td>
                    <td className="px-4 py-3">
                      <button
                        data-testid="payment-delete-button"
                        onClick={() => deletePayment(p)}
                        className="text-clay-muted hover:text-red-400 transition-colors p-1.5"
                        aria-label="Delete payment record"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <p className="font-mono text-[11px] uppercase tracking-[0.2em] text-clay-muted mt-4">
            Every membership, donation and sponsorship payment lands here — deleting a row only removes the record, never the real payment in Square
          </p>
        </section>
      </div>
      )}
      {view === "gallery" && <AdminGallery token={token} />}
      {view === "fixtures" && <AdminFixtures token={token} />}
      {view === "nominations" && <AdminNominations token={token} />}
      {view === "committee" && <AdminCommittee token={token} />}
      {view === "sponsors" && <AdminSponsors token={token} />}
      {view === "stats" && <AdminStats token={token} />}
      {view === "news" && <AdminNews token={token} />}
      {view === "volunteers" && <AdminVolunteers token={token} />}
      {view === "program" && <AdminProgram token={token} />}

      {emailTarget && (
        <div
          data-testid="member-email-modal"
          className="fixed inset-0 z-[70] bg-black/80 backdrop-blur-sm flex items-center justify-center p-4"
          onClick={() => !emailBusy && setEmailTarget(null)}
        >
          <div
            className="w-full max-w-lg bg-clay-surface border border-clay-border p-6 sm:p-8"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-start justify-between mb-6">
              <div>
                <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-clay-orange mb-1">
                  {emailTarget === "all" ? `All Members (${members.length})` : emailTarget.name}
                </p>
                <h3 className="font-display font-bold uppercase text-2xl tracking-tight">
                  {emailTarget === "all" ? "Bulk Email" : "Send Email"}
                </h3>
              </div>
              <button
                data-testid="member-email-close-button"
                onClick={() => !emailBusy && setEmailTarget(null)}
                className="text-clay-muted hover:text-clay-text transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            {emailTarget === "all" && (
              <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-amber-400/90 border border-amber-400/30 bg-amber-400/10 px-3 py-2 mb-4">
                This emails every member in the list — check before sending
              </p>
            )}
            <input
              data-testid="member-email-subject-input"
              placeholder="Subject *"
              value={emailForm.subject}
              onChange={(e) => setEmailForm((f) => ({ ...f, subject: e.target.value }))}
              className="w-full bg-clay-bg border border-clay-border focus:border-clay-orange outline-none px-4 py-3 text-clay-text placeholder:text-clay-muted/60 transition-colors mb-3"
            />
            <textarea
              data-testid="member-email-body-input"
              placeholder="Message * (blank lines split paragraphs)"
              value={emailForm.body}
              onChange={(e) => setEmailForm((f) => ({ ...f, body: e.target.value }))}
              rows={7}
              className="w-full bg-clay-bg border border-clay-border focus:border-clay-orange outline-none px-4 py-3 text-clay-text placeholder:text-clay-muted/60 transition-colors mb-4 resize-y"
            />
            <button
              data-testid="member-email-send-button"
              onClick={sendMemberEmail}
              disabled={emailBusy || !emailForm.subject.trim() || !emailForm.body.trim()}
              className="w-full bg-clay-orange hover:bg-clay-gold disabled:opacity-40 disabled:cursor-not-allowed text-clay-bg font-display font-bold uppercase tracking-wider px-6 py-3.5 transition-colors flex items-center justify-center gap-2"
            >
              {emailBusy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Mail className="w-4 h-4" />}
              {emailBusy ? "Sending…" : emailTarget === "all" ? `Send to ${members.length} Members` : "Send Email"}
            </button>
          </div>
        </div>
      )}
    </main>
  );
}
